import { theme } from 'antd';
import { DeletedFilled, MailIcon, UserIcon } from 'assets/images/svg';
import { forbiddenDomains } from 'config/forbiddenDomains';
import { invitedStatus } from 'config/invitedStatus';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeople, selectSelectedUsers } from 'reduxStore/people/selectors';
import { selectSites } from 'reduxStore/sites/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import { Alert, Avatar, Button, Col, Empty, Flex, Form, Input, List, Row, Select, Tag, Typography } from 'zui/Antd';
import { SelectFontSizeLG } from 'zui/Antd/Select';
import { useManagePermittedRoles } from 'library/hooks';

const { Text } = Typography;

const AddPeopleForm = ({ selectedSegmented = 'Zezamii Users', defaultSiteID = null }) => {
	//✅ Hooks
	const {
		token: {
			fontSizeSM,
			paddingXS,
			paddingSM,
			colorText,
			fontWeightStrong,
			colorTextLabel,
			colorIcon,
			colorBorder,
			borderMD = 6,
			paddingXXS,
			paddingXL,
			colorPrimaryBg,
			colorPrimary
		}
	} = theme.useToken();
	const [form] = Form.useForm();
	const dispatch = useDispatch();

	// 🛡️ Multi-tenancy: Get permitted sites
	const permittedSites = useSelector(selectSites);
	const isGlobalAdmin = useManagePermittedRoles('manageSiteAssignment');

	//✅ Redux state
	const people = useSelector(selectPeople);
	const selectedUsers = useSelector(selectSelectedUsers);

	//✅ Local state
	const [alert, setAlert] = useState({ message: '', type: '' });

	// 🎯 Native Form Initialization for Site Selection
	useEffect(() => {
		if (isGlobalAdmin) return; // Global admin can create without site
		const initialSite = defaultSiteID || (permittedSites?.length > 0 ? permittedSites[0].siteID : null);
		if (initialSite) {
			form.setFieldsValue({ siteID: initialSite });
		}
	}, [defaultSiteID, permittedSites, form, isGlobalAdmin]);

	//✅ Function to add users by name
	const handleAddUserByName = async () => {
		try {
			const values = await form.validateFields();
			const { firstName, lastName, siteID, siteIDs: formSiteIDs } = values;

			const resolvedSiteIDs = isGlobalAdmin ? formSiteIDs || [] : siteID ? [siteID] : [];
			const siteNames =
				resolvedSiteIDs
					.map((id) => permittedSites?.find((s) => s.siteID === id)?.name)
					.filter(Boolean)
					.join(', ') || 'No Site';

			const newUser = {
				personID: `${Date.now()}-${Math.random()}`,
				firstName,
				lastName,
				type: 'name',
				status: invitedStatus.PENDING,
				siteID: resolvedSiteIDs[0] || null,
				siteIDs: resolvedSiteIDs,
				siteName: siteNames
			};

			dispatch(peopleActions.addSelectedUser(newUser));
			form.resetFields(['firstName', 'lastName']);
			setAlert({ message: '', type: '' });
		} catch (error) {
			console.error('Validation failed:', error);
		}
	};

	//✅ Function to add users by email
	const handleAddUserByEmail = async () => {
		try {
			const values = await form.validateFields();
			const { email, siteID, siteIDs: formSiteIDs } = values;

			const resolvedSiteIDs = isGlobalAdmin ? formSiteIDs || [] : siteID ? [siteID] : [];
			const siteNames =
				resolvedSiteIDs
					.map((id) => permittedSites?.find((s) => s.siteID === id)?.name)
					.filter(Boolean)
					.join(', ') || 'No Site';

			const emails = email.split(',').map((e) => e.trim());
			const validEmails = [...new Set(emails)];

			const errors = [];
			let hasNewEmails = false;

			for (const e of validEmails) {
				const domain = e.split('@')[1]?.toLowerCase();

				// validate email not include prohibited domains
				if (forbiddenDomains.includes(domain)) {
					errors.push(`Domain not allowed: ${domain}`);
					continue;
				}

				// validate emails not include existing people emails
				const existsInPeople = people.find((person) => person.email === e);
				if (existsInPeople) {
					errors.push(`Already exists in organisation: ${e}`);
					continue;
				}

				// Check if email already exists in selected users
				const existsInSelected = selectedUsers.find((user) => user.email === e);
				if (existsInSelected) {
					errors.push(`Already in the list: ${e}`);
					continue;
				}

				// Add email to Redux store
				const newUser = {
					personID: `${Date.now()}-${Math.random()}`,
					email: e,
					status: invitedStatus.PENDING,
					type: 'email',
					siteID: resolvedSiteIDs[0] || null,
					siteIDs: resolvedSiteIDs,
					siteName: siteNames
				};

				dispatch(peopleActions.addSelectedUser(newUser));
				hasNewEmails = true;
			}

			if (errors.length > 0) {
				setAlert({ message: errors.join('<br/>'), type: 'warning' });
			} else if (hasNewEmails) {
				setAlert({ message: '', type: '' });
			}

			// Clear ONLY the email input, keep the selected site
			form.resetFields(['email']);
		} catch (error) {
			console.error('Validation failed:', error);
		}
	};

	//✅ Function to clear the list
	const handleClearList = () => {
		dispatch(peopleActions.clearSelectedUsers());
	};

	//✅ Function to delete a specific user
	const handleDeleteUser = (personID) => {
		dispatch(peopleActions.removeSelectedUser({ personID }));
	};

	//✅ Determine if we are adding by email or name
	const isAddingByEmail = selectedSegmented === 'Zezamii Users';

	const description = isAddingByEmail
		? 'Add email addresses to this list to invite them to the platform'
		: 'Add people to this list to add them to the platform';

	return (
		<Form form={form} layout='vertical'>
			<Row gutter={[paddingXS, 0]}>
				{isAddingByEmail ? (
					// Form for adding by Email
					<Col span={24}>
						<Form.Item
							name='email'
							label={<Text strong>Invite Email Addresses</Text>}
							rules={fieldsValidator.MultiEmail}
							style={{ marginBottom: paddingXL }}
						>
							<Flex gap={paddingXS}>
								<Input
									placeholder='Enter multiple emails separated by commas or semicolons'
									prefix={<MailIcon color={colorIcon} style={{ marginRight: paddingXXS }} />}
									onPressEnter={handleAddUserByEmail}
									onChange={() => setAlert({ message: '', type: '' })}
								/>
								<Button type='primary' onClick={handleAddUserByEmail}>
									Add All
								</Button>
							</Flex>
						</Form.Item>
					</Col>
				) : (
					// Form for adding by Name
					<>
						<Col span={12}>
							<Form.Item
								name='firstName'
								label={<Text strong>First Name</Text>}
								rules={fieldsValidator.Name}
								style={{ marginBottom: paddingXL }}
							>
								<Input
									placeholder='John'
									prefix={<UserIcon color={colorIcon} style={{ marginRight: paddingXXS }} />}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name='lastName'
								label={<Text strong>Last Name</Text>}
								rules={fieldsValidator.Name}
								style={{ marginBottom: paddingXL }}
							>
								<Flex gap={paddingXS}>
									<Input
										placeholder='Doe'
										prefix={<UserIcon color={colorIcon} style={{ marginRight: paddingXXS }} />}
										onPressEnter={handleAddUserByName}
									/>
									<Button type='primary' onClick={handleAddUserByName}>
										Add
									</Button>
								</Flex>
							</Form.Item>
						</Col>
					</>
				)}

				{/* 📍 MULTI-TENANT SITE SELECTOR (Native Form Item) */}
				<Col span={24}>
					{isGlobalAdmin ? (
						<Form.Item
							name='siteIDs'
							label={<Text strong>Select Sites</Text>}
							style={{ marginBottom: paddingXL }}
						>
							<SelectFontSizeLG
								mode='multiple'
								placeholder='Select Sites'
								options={permittedSites?.map((s) => ({
									label: s.name,
									value: s.siteID
								}))}
								showSearch={false}
								virtual={false}
								maxTagCount={2}
								maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
								$tagColor={colorPrimary}
								style={{ width: '100%' }}
								allowClear
							/>
						</Form.Item>
					) : (
						<Form.Item
							name='siteID'
							label={<Text strong>Select Site</Text>}
							rules={[{ required: true, message: 'Please select a site to assign these users.' }]}
							style={{ marginBottom: paddingXL }}
							extra={
								permittedSites?.length <= 1 && (
									<Text type='secondary' style={{ fontSize: 12 }}>
										People will automatically be added to your assigned site.
									</Text>
								)
							}
						>
							<Select
								placeholder='Select a site'
								disabled={permittedSites?.length <= 1 || !!defaultSiteID}
								options={permittedSites?.map((s) => ({
									label: s.name,
									value: s.siteID
								}))}
							/>
						</Form.Item>
					)}
				</Col>

				<Col span={24}>
					{alert.message && (
						<Alert
							message={<div dangerouslySetInnerHTML={{ __html: alert.message }} />}
							type={alert.type || 'warning'}
							showIcon
							closable
							onClose={() => setAlert({ message: '', type: '' })}
							style={{ marginBottom: paddingXS, fontSize: fontSizeSM }}
						/>
					)}
				</Col>

				{/* List of added users */}
				<Col span={24}>
					<Flex gap={paddingSM} vertical>
						<Flex align='center' justify='space-between'>
							<Text strong>{isAddingByEmail ? 'Selected Email Addresses' : 'Selected People'}</Text>
							<Button type='text' onClick={handleClearList} style={{ textDecoration: 'underline' }}>
								Clear List
							</Button>
						</Flex>

						<Flex
							style={{
								height: 145,
								overflowY: 'auto',
								padding: `${paddingSM}px 0px`,
								borderRadius: borderMD,
								border: `1px solid ${colorBorder}`,
								width: 'calc(100% - 2px)'
							}}
						>
							{selectedUsers && selectedUsers.length > 0 ? (
								<List
									dataSource={selectedUsers}
									split={false}
									style={{ width: '100%', padding: `0px ${paddingXS}px` }}
									renderItem={(user, index) => {
										const isBeingProcessed = user.status === invitedStatus.INVITING;

										return (
											<List.Item
												key={user.personID}
												style={{
													padding: `${paddingXXS}px ${paddingSM}px`,
													width: '100%',
													borderBottom: 'none',
													marginBottom: index < selectedUsers.length - 1 ? paddingXXS : 0,
													backgroundColor: isBeingProcessed ? '#E3F2FD' : 'transparent',
													borderRadius: borderMD
												}}
												extra={
													<Flex align='center' gap={paddingXXS}>
														{user.status === invitedStatus.INVITING && (
															<Tag
																color={colorPrimaryBg}
																bordered={false}
																style={{
																	fontWeight: fontWeightStrong,
																	color: colorPrimary
																}}
															>
																{selectedSegmented === 'Zezamii Users'
																	? 'Inviting'
																	: 'Adding'}
															</Tag>
														)}
														{user.status === invitedStatus.INVITED && (
															<Tag
																color='green'
																bordered={false}
																style={{ fontWeight: fontWeightStrong }}
															>
																{selectedSegmented === 'Zezamii Users'
																	? 'Invited'
																	: 'Added'}
															</Tag>
														)}
														{user.status === invitedStatus.PENDING && (
															<Button
																icon={<DeletedFilled width={16} height={16} />}
																onClick={() => handleDeleteUser(user.personID)}
																type='text'
																style={{ color: colorTextLabel }}
															/>
														)}
													</Flex>
												}
											>
												<List.Item.Meta
													avatar={
														<Avatar
															size={30}
															style={{
																fontSize: fontSizeSM,
																color: colorText,
																fontWeight: fontWeightStrong,
																backgroundColor: '#D9D9D9'
															}}
														>
															{user.type === 'name'
																? user.firstName?.charAt(0).toUpperCase()
																: user.email?.charAt(0).toUpperCase()}
														</Avatar>
													}
													title={
														<Text style={{ fontWeight: 400 }}>
															{user.type === 'name'
																? `${user.firstName} ${user.lastName}`
																: user.email}
														</Text>
													}
													// 📍 NEW: Render assigned Site Name under the user
													description={
														<Text type='secondary' style={{ fontSize: 12 }}>
															{user.siteName}
														</Text>
													}
												/>
											</List.Item>
										);
									}}
								/>
							) : (
								<Flex align='center' justify='center' style={{ height: '100%', width: '100%' }}>
									<Empty description={description} image={Empty.PRESENTED_IMAGE_SIMPLE} />
								</Flex>
							)}
						</Flex>
					</Flex>
				</Col>
			</Row>
		</Form>
	);
};

export default AddPeopleForm;
